import React, { useEffect, useMemo, useState } from 'react'
import {
  Box,
  Link,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TablePagination,
  TableRow,
  CircularProgress,
} from '@mui/material'
import useSWR from 'swr'
import { Link as RouterLink, useNavigate, useSearchParams } from 'react-router-dom'

// project imports
import EnhancedTableHeader from './EnhancedTableHeader'
import EnhancedTableToolbar, { JobsFilters, defaultJobsFilters } from './EnhancedTableToolbar'
import JobDetailsModal from './JobDetailsModal'
import axiosInstance from '../../../utils/axiosInstance'
import { Job } from '../../../types/job'
import { useSelector } from '../../../store/store'
import { isEndCustomer } from '../../../constants'

// Define table headers
const headCells = [
  { id: 'externalId', label: 'External ID' },
  { id: 'customer', label: 'Customer' },
  { id: 'title', label: 'Job Title' },
  { id: 'location', label: 'Location' },
  { id: 'order', label: 'Linked order' },
  { id: 'externalCreatedAt', label: 'Created at' },
  { id: 'importedAt', label: 'Imported at' },
]

export default function JobsTable() {
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(10)
  const [filters, setFilters] = useState<JobsFilters>(defaultJobsFilters)
  const [debouncedSearch, setDebouncedSearch] = useState(filters.search)
  const [searchParams, setSearchParams] = useSearchParams()
  const navigate = useNavigate()
  const { role } = useSelector((state) => state.user.userInfo)

  // End customers only ever see their own jobs, so the customer column is noise.
  const showCustomer = !isEndCustomer(role)
  const visibleHeadCells = showCustomer
    ? headCells
    : headCells.filter((headCell) => headCell.id !== 'customer')

  // Debounce the free-text search so we don't fire a request per keystroke.
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(filters.search), 400)
    return () => clearTimeout(timer)
  }, [filters.search])

  const handleFiltersChange = (next: JobsFilters) => {
    setFilters(next)
    setPage(0)
  }

  const jobsUrl = useMemo(() => {
    const params = new URLSearchParams({
      page: String(page + 1),
      limit: String(rowsPerPage),
    })
    if (debouncedSearch.trim()) params.set('search', debouncedSearch.trim())
    if (filters.customerId) params.set('customerId', filters.customerId)
    if (filters.linkStatus !== 'all') params.set('linkStatus', filters.linkStatus)
    return `/jobs?${params.toString()}`
  }, [page, rowsPerPage, debouncedSearch, filters.customerId, filters.linkStatus])

  const { data, isLoading, mutate } = useSWR(jobsUrl, (url) =>
    axiosInstance.get(url).then((res) => res.data)
  )

  const selectedJobId = searchParams.get('jobId')
  const jobs = data?.jobs || []
  const selectedJobInList = jobs.find((job: Job) => job.id === selectedJobId)

  // A job opened via ?jobId= (e.g. from an order page) is usually not on the current
  // table page, so fetch it on its own.
  const { data: fetchedSelectedJob, mutate: mutateSelectedJob } = useSWR(
    selectedJobId && !isLoading && !selectedJobInList ? `/jobs/${selectedJobId}` : null,
    (url) => axiosInstance.get(url).then((res) => res.data)
  )
  const selectedJob: Job | null = selectedJobInList || fetchedSelectedJob || null

  const totalCount = data?.totalCount || 0

  const handleChangePage = (_: unknown, newPage: number) => {
    setPage(newPage)
  }

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10))
    setPage(0)
  }

  const handleRowClick = (jobId: string) => {
    setSearchParams({ jobId })
    navigate(`?jobId=${jobId}`, { replace: true })
  }

  const handleCloseModal = () => {
    setSearchParams({})
    navigate('', { replace: true })
  }

  const renderTableContent = () => {
    if (isLoading) {
      return (
        <TableRow>
          <TableCell colSpan={visibleHeadCells.length} align="center" sx={{ py: 3 }}>
            <CircularProgress />
          </TableCell>
        </TableRow>
      )
    }

    if (jobs.length === 0) {
      return (
        <TableRow>
          <TableCell colSpan={visibleHeadCells.length} align="center" sx={{ py: 3 }}>
            No jobs found
          </TableCell>
        </TableRow>
      )
    }

    return jobs.map((job: Job) => (
      <TableRow
        key={job.id}
        hover
        onClick={() => handleRowClick(job.id)}
        sx={{ cursor: 'pointer' }}
      >
        <TableCell>{job.externalId || job.referenceNumber}</TableCell>
        {showCustomer && <TableCell>{job.customer?.name}</TableCell>}
        <TableCell>{job.title}</TableCell>
        <TableCell>{job.location}</TableCell>
        <TableCell>
          {job.order ? (
            <Link
              component={RouterLink}
              to={`/orders/${job.order.id}`}
              // Keep the row's click (open the job) from firing as well.
              onClick={(event) => event.stopPropagation()}
            >
              #{job.order.displayId} - {job.order.jobName}
            </Link>
          ) : (
            '-'
          )}
        </TableCell>
        <TableCell>
          {job.externalCreatedAt ? new Date(job.externalCreatedAt).toLocaleDateString() : '-'}
        </TableCell>
        <TableCell>
          {job.importedAt ? new Date(job.importedAt).toLocaleDateString() : '-'}
        </TableCell>
      </TableRow>
    ))
  }

  return (
    <Box>
      <Paper sx={{ width: '100%', mb: 2 }}>
        <EnhancedTableToolbar filters={filters} onChange={handleFiltersChange} />
        <TableContainer>
          <Table sx={{ minWidth: 750 }} aria-labelledby="tableTitle">
            <EnhancedTableHeader headCells={visibleHeadCells} />
            <TableBody>{renderTableContent()}</TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={totalCount}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Paper>
      <JobDetailsModal
        job={selectedJob}
        open={!!selectedJobId}
        onClose={handleCloseModal}
        onSaved={() => {
          mutate()
          mutateSelectedJob()
        }}
      />
    </Box>
  )
}
